import { useState, useEffect } from "react";
import axios from "axios";
import { Plus, Edit2, Trash2, Save, FileText } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Card, CardContent } from "@/components/ui/card";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import { toast } from "sonner";
import AdminLayout from "@/components/layout/AdminLayout";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const getAuthHeaders = () => ({
  Authorization: `Bearer ${localStorage.getItem("admin_token")}`,
});

export default function AdminPages() {
  const [pages, setPages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingPage, setEditingPage] = useState(null);
  const [formData, setFormData] = useState({
    slug: "",
    title: "",
    content: "",
    meta_title: "",
    meta_description: "",
    is_active: true,
  });

  const fetchPages = async () => {
    try {
      const response = await axios.get(`${API}/pages`, { headers: getAuthHeaders() });
      setPages(response.data);
    } catch (error) {
      toast.error("Sayfalar yüklenemedi");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchPages();
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingPage) {
        await axios.put(`${API}/pages/${editingPage.id}`, formData, {
          headers: getAuthHeaders(),
        });
        toast.success("Sayfa güncellendi");
      } else {
        await axios.post(`${API}/pages`, formData, {
          headers: getAuthHeaders(),
        });
        toast.success("Sayfa eklendi");
      }
      setDialogOpen(false);
      resetForm();
      fetchPages();
    } catch (error) {
      toast.error("İşlem başarısız");
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Bu sayfayı silmek istediğinize emin misiniz?")) return;
    try {
      await axios.delete(`${API}/pages/${id}`, {
        headers: getAuthHeaders(),
      });
      toast.success("Sayfa silindi");
      fetchPages();
    } catch (error) {
      toast.error("Silme işlemi başarısız");
    }
  };

  const handleEdit = (page) => {
    setEditingPage(page);
    setFormData({
      slug: page.slug,
      title: page.title,
      content: page.content,
      meta_title: page.meta_title || "",
      meta_description: page.meta_description || "",
      is_active: page.is_active,
    });
    setDialogOpen(true);
  };

  const resetForm = () => {
    setEditingPage(null);
    setFormData({
      slug: "",
      title: "",
      content: "",
      meta_title: "",
      meta_description: "",
      is_active: true,
    });
  };

  return (
    <AdminLayout title="Sayfalar">
      <div className="space-y-6" data-testid="admin-pages">
        <div className="flex justify-between items-center">
          <p className="text-slate-600">Impressum, Datenschutz gibi statik sayfaları yönetin.</p>
          <Button
            onClick={() => {
              resetForm();
              setDialogOpen(true);
            }}
            className="bg-electric-500 hover:bg-electric-600"
            data-testid="add-page-btn"
          >
            <Plus className="w-4 h-4 mr-2" />
            Yeni Sayfa
          </Button>
        </div>

        {loading ? (
          <div className="space-y-4">
            {[...Array(3)].map((_, i) => (
              <div key={i} className="h-20 bg-slate-100 rounded-lg animate-pulse"></div>
            ))}
          </div>
        ) : (
          <div className="space-y-3">
            {pages.map((page) => (
              <Card key={page.id} className="border-0 shadow-sm">
                <CardContent className="p-4">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-4">
                      <div className="w-10 h-10 bg-electric-100 rounded-lg flex items-center justify-center">
                        <FileText className="w-5 h-5 text-electric-600" />
                      </div>
                      <div>
                        <h3 className="font-semibold text-navy-900">{page.title}</h3>
                        <p className="text-sm text-slate-500">/{page.slug}</p>
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      <span className={`px-2 py-1 text-xs rounded-full ${page.is_active ? "bg-signal-100 text-signal-700" : "bg-slate-100 text-slate-500"}`}>
                        {page.is_active ? "Aktif" : "Pasif"}
                      </span>
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={() => handleEdit(page)}
                      >
                        <Edit2 className="w-4 h-4" />
                      </Button>
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={() => handleDelete(page.id)}
                        className="text-red-500 hover:text-red-600 hover:bg-red-50"
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}

        <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
          <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>
                {editingPage ? "Sayfa Düzenle" : "Yeni Sayfa"}
              </DialogTitle>
            </DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Sayfa Başlığı *</Label>
                  <Input
                    value={formData.title}
                    onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                    placeholder="Impressum"
                    required
                  />
                </div>
                <div className="space-y-2">
                  <Label>Slug (URL) *</Label>
                  <Input
                    value={formData.slug}
                    onChange={(e) => setFormData({ ...formData, slug: e.target.value })}
                    placeholder="impressum"
                    required
                  />
                </div>
              </div>

              <div className="space-y-2">
                <Label>İçerik (HTML) *</Label>
                <Textarea
                  value={formData.content}
                  onChange={(e) => setFormData({ ...formData, content: e.target.value })}
                  placeholder="<h1>Impressum</h1>..."
                  rows={12}
                  required
                />
                <p className="text-xs text-slate-500">HTML formatında içerik girebilirsiniz.</p>
              </div>

              <div className="space-y-2">
                <Label>Meta Başlık (SEO)</Label>
                <Input
                  value={formData.meta_title}
                  onChange={(e) => setFormData({ ...formData, meta_title: e.target.value })}
                  placeholder="Impressum - BestTariffeSed.de"
                />
              </div>

              <div className="space-y-2">
                <Label>Meta Açıklama (SEO)</Label>
                <Textarea
                  value={formData.meta_description}
                  onChange={(e) => setFormData({ ...formData, meta_description: e.target.value })}
                  placeholder="SEO açıklaması"
                  rows={2}
                />
              </div>

              <div className="flex items-center gap-2">
                <Switch
                  checked={formData.is_active}
                  onCheckedChange={(checked) => setFormData({ ...formData, is_active: checked })}
                />
                <Label>Aktif</Label>
              </div>

              <DialogFooter>
                <Button type="button" variant="outline" onClick={() => setDialogOpen(false)}>
                  İptal
                </Button>
                <Button type="submit" className="bg-electric-500 hover:bg-electric-600">
                  <Save className="w-4 h-4 mr-2" />
                  Kaydet
                </Button>
              </DialogFooter>
            </form>
          </DialogContent>
        </Dialog>
      </div>
    </AdminLayout>
  );
}
